import { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef, ReactNode } from 'react';
import { patch } from '@use-gpu/state';
import { useQueryClient } from '@tanstack/react-query';
import { Chat, ChatMessageTypeEnum } from '@common/api';
import { chatApi } from '@data/rest/api';
import { useI18nContext } from '@i18n/i18n-react';
import toast from 'react-hot-toast';
import { useAuth } from '@providers/AuthProvider';
import { LayoutMode } from '@features/chat/chatTypes';
import { useChatLayoutMode, useLastChatId } from '@components/chat/localChatHistory';
import { useAnimatedToggle } from '@hooks/useAnimatedToggle';
import { useChatStreamApi } from '@features/chat/hooks/useChatStreamApi';
import { useChatMessages, chatMessageKeys } from '@features/chat/hooks/useChatMessages';
import { useChats, CHATS_KEY } from '@features/chat/hooks/useChats';
import { DisplayedMessage, DraftChatMessage } from '@features/chat/chatTypes';
import { DEFAULT_AGENT_TYPE } from '@features/chat/agentTypes';
import { handleApiErrorMessage } from '@util/message';

const ANIMATION_DURATION = 200;

interface AIChatLayoutContextType {
    isChatOpen: boolean;
    isClosing: boolean;
    layoutMode: LayoutMode;
    openChat: () => void;
    closeChat: () => void;
    toggleLayoutMode: () => void;
}

const AIChatLayoutContext = createContext<AIChatLayoutContextType | null>(null);

export function useAIChat(): AIChatLayoutContextType {
    const context = useContext(AIChatLayoutContext);
    if (!context) {
        throw new Error('useAIChat must be used within an AIChatProvider');
    }
    return context;
}

interface ChatLocalState {
    currentChatId: number | null;
    message: string;
    activeRequestId: number | null;
    streamingStatus: string | null;
    streamingContent: string;
    isStreaming: boolean;
    streamingMessages: DisplayedMessage[] | null;
}

const INITIAL_STATE: ChatLocalState = {
    currentChatId: null,
    message: '',
    activeRequestId: null,
    streamingStatus: null,
    streamingContent: '',
    isStreaming: false,
    streamingMessages: null,
};

function updateLastReply(messages: DisplayedMessage[], content: string): DisplayedMessage[] {
    return messages.map((m, idx) =>
        idx === messages.length - 1 && m.type === ChatMessageTypeEnum.Assistant ? { ...m, content } : m
    );
}

export interface AIChatStateContextType {
    currentChatId: number | null;
    message: string;
    messages: DisplayedMessage[];
    chats: Chat[];
    isLoadingMessages: boolean;
    streamingStatus: string | null;
    isStreaming: boolean;
    isSendingMessage: boolean;
    currentChat: Chat | undefined;
    setCurrentChatId: (id: number | null) => void;
    setMessage: (msg: string) => void;
    sendMessage: (message: DraftChatMessage & { type: typeof ChatMessageTypeEnum.User }, requestId: number) => void;
    cancelRequest: () => void;
}

const AIChatStateContext = createContext<AIChatStateContextType | null>(null);

export function useChatContext(): AIChatStateContextType {
    const context = useContext(AIChatStateContext);
    if (!context) {
        throw new Error('useChatContext must be used within an AIChatProvider');
    }
    return context;
}

interface AIChatProviderProps {
    children: ReactNode;
}

export function AIChatProvider({ children }: AIChatProviderProps) {
    // Layout state
    const [isOpen, isClosing, chat] = useAnimatedToggle(ANIMATION_DURATION);
    const [layoutMode, setLayoutMode] = useChatLayoutMode();

    const toggleLayoutMode = useCallback(() => {
        const newMode = layoutMode === 'modal' ? 'fullscreen' : 'modal';
        setLayoutMode(newMode);
    }, [layoutMode, setLayoutMode]);

    const layoutValue = useMemo(
        () => ({
            isChatOpen: isOpen,
            isClosing,
            layoutMode,
            openChat: chat.on,
            closeChat: chat.off,
            toggleLayoutMode,
        }),
        [isOpen, isClosing, layoutMode, chat.on, chat.off, toggleLayoutMode]
    );

    // Chat state
    const [lastChatId, setLastChatId] = useLastChatId();
    const [state, setState] = useState<ChatLocalState>({
        ...INITIAL_STATE,
        currentChatId: lastChatId ?? null,
    });

    const { LL } = useI18nContext();
    const queryClient = useQueryClient();
    const { tenant } = useAuth();

    const isChatActive = isOpen || state.isStreaming || state.activeRequestId !== null;
    const { data: chats = [] } = useChats(isChatActive);
    const isSendingMessage = state.activeRequestId !== null;
    const { data: fetchedMessages = [], isLoading: isLoadingMessages } = useChatMessages(
        state.currentChatId,
        isChatActive
    );

    // Refs for stable callback identities (avoid stale closures in sendMessage/cancelRequest)
    const fetchedMessagesRef = useRef(fetchedMessages);
    fetchedMessagesRef.current = fetchedMessages;

    const stateRef = useRef(state);
    stateRef.current = state;

    const chatsRef = useRef(chats);
    chatsRef.current = chats;

    const messages = state.streamingMessages ?? fetchedMessages;
    const currentChat = useMemo(() => chats.find((c) => c.id === state.currentChatId), [chats, state.currentChatId]);

    useEffect(() => {
        if (state.currentChatId !== null) {
            setLastChatId(state.currentChatId);
        }
    }, [state.currentChatId, setLastChatId]);

    const streamCallbacks = useMemo(
        () => ({
            onStep: (text: string) => {
                setState((s) => {
                    if (s.activeRequestId === null) return s;
                    return patch(s, { streamingStatus: text, isStreaming: true });
                });
            },
            onToken: (token: string) => {
                setState((s) => {
                    const newContent = s.streamingContent + token;
                    if (!s.streamingMessages) return s;
                    return patch(s, {
                        streamingContent: newContent,
                        streamingStatus: null,
                        streamingMessages: updateLastReply(s.streamingMessages, newContent),
                    });
                });
            },
            onComplete: (responseJson: string) => {
                setState((s) => {
                    let updatedMessages = s.streamingMessages;
                    try {
                        const response = JSON.parse(responseJson);
                        if (updatedMessages) {
                            updatedMessages = updateLastReply(updatedMessages, response.response);
                        }
                    } catch (e) {
                        console.error('Failed to parse complete event response:', e);
                    }
                    return patch(s, {
                        streamingMessages: updatedMessages,
                        isStreaming: false,
                        streamingStatus: null,
                        streamingContent: '',
                    });
                });
                Promise.all([
                    queryClient.invalidateQueries(chatMessageKeys.all),
                    queryClient.invalidateQueries([CHATS_KEY]),
                ])
                    .catch(() => {})
                    .finally(() => {
                        setState((s) => patch(s, { streamingMessages: null, activeRequestId: null }));
                    });
            },
            onError: (error: string) => {
                setState((s) => {
                    const updatedMessages = s.streamingMessages
                        ? updateLastReply(s.streamingMessages, error)
                        : s.streamingMessages;
                    return patch(s, {
                        streamingMessages: updatedMessages,
                        isStreaming: false,
                        streamingStatus: null,
                        streamingContent: '',
                    });
                });
                queryClient
                    .invalidateQueries(chatMessageKeys.all)
                    .catch(() => {})
                    .finally(() => {
                        setState((s) => patch(s, { streamingMessages: null, activeRequestId: null }));
                    });
            },
        }),
        [queryClient]
    );

    const { sendStreamingMessage, cancelStream } = useChatStreamApi(streamCallbacks);

    // Abort SSE connection on provider unmount
    useEffect(() => {
        return () => cancelStream();
    }, [cancelStream]);

    const setCurrentChatId = useCallback(
        (id: number | null) =>
            setState((s) => (s.isStreaming || s.activeRequestId !== null ? s : patch(s, { currentChatId: id }))),
        []
    );
    const setMessage = useCallback((msg: string) => setState((s) => patch(s, { message: msg })), []);

    const sendMessage = useCallback(
        async (message: DraftChatMessage & { type: typeof ChatMessageTypeEnum.User }, requestId: number) => {
            if (stateRef.current.activeRequestId !== null) {
                toast.error(LL.PREVIOUS_MESSAGE_IS_PROCESSING());
                return;
            }

            const { currentChatId } = stateRef.current;
            const chatExists = chatsRef.current.some((c) => c.id === currentChatId);

            setState((s) => patch(s, { activeRequestId: requestId }));

            let targetChatId = currentChatId;
            if (!currentChatId || !chatExists) {
                try {
                    const { chatId } = await chatApi.create({ agentType: DEFAULT_AGENT_TYPE });
                    targetChatId = chatId;
                    setState((s) => patch(s, { currentChatId: chatId }));
                    queryClient.invalidateQueries([CHATS_KEY]);
                    toast.success(LL.CHAT_CREATED());
                } catch {
                    toast.error(LL.CHAT_FAILED_CREATE());
                    setState((s) => patch(s, { activeRequestId: null }));
                    return;
                }
            }

            const currentMessages = fetchedMessagesRef.current;
            const streamingMessages = [
                ...currentMessages,
                message,
                { content: '', type: ChatMessageTypeEnum.Assistant },
            ];

            setState((s) =>
                patch(s, {
                    message: '',
                    streamingMessages,
                    streamingStatus: LL.CHAT_STATUS_ANALYZING_CONTEXT(),
                    isStreaming: true,
                })
            );

            const isFirstMessage = currentMessages.length === 0;

            try {
                await sendStreamingMessage(targetChatId!, message.content.trim(), requestId, tenant!);
                queryClient.invalidateQueries([CHATS_KEY]);

                if (isFirstMessage) {
                    setTimeout(() => queryClient.invalidateQueries([CHATS_KEY]), 2500);
                }
            } catch {
                setState((s) => patch(s, { activeRequestId: null }));
            }
        },
        [LL, queryClient, sendStreamingMessage, tenant]
    );

    const cancelRequest = useCallback(async () => {
        const { activeRequestId } = stateRef.current;
        if (!activeRequestId) return;
        setState((s) => patch(s, { streamingStatus: null, isStreaming: false }));
        try {
            await chatApi.cancel(activeRequestId);
        } catch (error) {
            setState((s) => patch(s, { activeRequestId: null, streamingMessages: null }));
            handleApiErrorMessage(error, LL.CHAT_FAILED_CANCEL());
        }
    }, [LL]);

    const stateValue = useMemo(
        () => ({
            currentChatId: state.currentChatId,
            message: state.message,
            messages,
            chats,
            isLoadingMessages: state.currentChatId === null ? false : isLoadingMessages,
            streamingStatus: state.streamingStatus,
            isStreaming: state.isStreaming,
            isSendingMessage,
            currentChat,
            setCurrentChatId,
            setMessage,
            sendMessage,
            cancelRequest,
        }),
        [
            state.currentChatId,
            state.message,
            messages,
            chats,
            isLoadingMessages,
            state.streamingStatus,
            state.isStreaming,
            isSendingMessage,
            currentChat,
            setCurrentChatId,
            setMessage,
            sendMessage,
            cancelRequest,
        ]
    );

    return (
        <AIChatLayoutContext.Provider value={layoutValue}>
            <AIChatStateContext.Provider value={stateValue}>{children}</AIChatStateContext.Provider>
        </AIChatLayoutContext.Provider>
    );
}
